import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { 
  ArrowLeft, Download, Users, CheckCircle, XCircle, Clock,
  BarChart3, TrendingUp, TrendingDown, PieChart, AlertTriangle,
  UserCheck, UserX, Zap, ChevronDown
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const AssessmentAnalyticsPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [updatingSelection, setUpdatingSelection] = useState(null);
  const [autoEvaluating, setAutoEvaluating] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('company_token');
    if (!token) {
      navigate('/company/auth');
      return;
    }
    fetchAnalytics(token);
  }, [id, navigate]);

  const fetchAnalytics = async (token) => {
    try {
      const res = await axios.get(`${API}/api/company/assessments/${id}/analytics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
    } catch (err) {
      toast.error('Failed to load analytics');
      navigate('/company/assessments');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const token = localStorage.getItem('company_token');
      const res = await axios.get(`${API}/api/company/assessments/${id}/export`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const exportData = res.data.export_data;
      if (!exportData || exportData.length === 0) {
        toast.error('No data to export');
        return;
      }
      
      const headers = Object.keys(exportData[0]);
      const csvContent = [
        headers.join(','),
        ...exportData.map(row => headers.map(h => `"${(row[h] || '').toString().replace(/"/g, '""')}"`).join(','))
      ].join('\n');
      
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `assessment_${id}_results.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success('Export downloaded successfully');
    } catch (err) {
      toast.error('Failed to export data');
    }
  };

  const updateCandidateSelection = async (candidateId, status) => {
    try {
      setUpdatingSelection(candidateId);
      const token = localStorage.getItem('company_token');
      await axios.put(`${API}/api/company/candidates/${candidateId}/selection`, {
        selection_status: status
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update local state
      setData(prev => ({
        ...prev,
        candidates: prev.candidates.map(c => 
          c.id === candidateId ? { ...c, selection_status: status } : c
        )
      }));
      
      toast.success(`Candidate marked as ${status}`);
    } catch (err) {
      toast.error('Failed to update selection');
    } finally {
      setUpdatingSelection(null);
    }
  };

  const handleAutoEvaluate = async () => {
    try {
      setAutoEvaluating(true);
      const token = localStorage.getItem('company_token');
      const res = await axios.post(`${API}/api/company/assessments/${id}/auto-evaluate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(res.data.message);
      fetchAnalytics(token); // Refresh data
    } catch (err) {
      toast.error('Failed to auto-evaluate');
    } finally {
      setAutoEvaluating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-purple-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!data) return null;

  const { assessment, analytics, candidates } = data;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate(`/company/assessments/${id}`)}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
              >
                <ArrowLeft size={20} />
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Analytics</h1>
                <p className="text-sm text-gray-500">{assessment.title}</p>
              </div>
            </div>
            
            <button
              onClick={handleExport}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium"
              data-testid="export-results-btn"
            >
              <Download size={18} />
              Export CSV
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Overview Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8">
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <p className="text-sm text-gray-500">Invited</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{analytics.total_invited}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <p className="text-sm text-gray-500">Started</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{analytics.total_started}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <p className="text-sm text-gray-500">Completed</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{analytics.total_completed}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <p className="text-sm text-gray-500">Completion Rate</p>
            <p className="text-2xl font-bold text-purple-600 mt-1">{analytics.completion_rate}%</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <p className="text-sm text-gray-500">Pass Rate</p>
            <p className="text-2xl font-bold text-green-600 mt-1">{analytics.pass_rate}%</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <p className="text-sm text-gray-500">Avg Score</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{analytics.average_percentage}%</p>
          </div>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Score Distribution */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Score Distribution</h3>
            <div className="space-y-2">
              {analytics.score_distribution?.map((bucket, idx) => {
                const maxCount = Math.max(...analytics.score_distribution.map(b => b.count));
                const width = maxCount > 0 ? (bucket.count / maxCount) * 100 : 0;
                return (
                  <div key={idx} className="flex items-center gap-3">
                    <span className="w-16 text-sm text-gray-600">{bucket.range}</span>
                    <div className="flex-1 bg-gray-100 rounded-full h-6 overflow-hidden">
                      <div 
                        className="h-full bg-purple-500 rounded-full transition-all"
                        style={{ width: `${width}%` }}
                      />
                    </div>
                    <span className="w-8 text-sm font-medium text-gray-900">{bucket.count}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Score Stats */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Performance Summary</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-green-50 rounded-lg">
                <div className="flex items-center gap-2 text-green-600 mb-1">
                  <TrendingUp size={18} />
                  <span className="text-sm font-medium">Highest Score</span>
                </div>
                <p className="text-2xl font-bold text-green-700">{analytics.highest_score}/{assessment.total_points}</p>
              </div>
              <div className="p-4 bg-red-50 rounded-lg">
                <div className="flex items-center gap-2 text-red-600 mb-1">
                  <TrendingDown size={18} />
                  <span className="text-sm font-medium">Lowest Score</span>
                </div>
                <p className="text-2xl font-bold text-red-700">{analytics.lowest_score}/{assessment.total_points}</p>
              </div>
              <div className="p-4 bg-blue-50 rounded-lg">
                <div className="flex items-center gap-2 text-blue-600 mb-1">
                  <BarChart3 size={18} />
                  <span className="text-sm font-medium">Average Score</span>
                </div>
                <p className="text-2xl font-bold text-blue-700">{analytics.average_score}/{assessment.total_points}</p>
              </div>
              <div className="p-4 bg-amber-50 rounded-lg">
                <div className="flex items-center gap-2 text-amber-600 mb-1">
                  <Clock size={18} />
                  <span className="text-sm font-medium">Avg Time</span>
                </div>
                <p className="text-2xl font-bold text-amber-700">{Math.round(analytics.average_time_seconds / 60)} min</p>
              </div>
            </div>

            {/* Proctoring Stats */}
            {analytics.tab_switch_stats && (
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2 text-gray-600 mb-2">
                  <AlertTriangle size={18} />
                  <span className="text-sm font-medium">Proctoring Stats</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Avg Tab Switches:</span>
                  <span className="font-medium text-gray-900">{analytics.tab_switch_stats.average}</span>
                </div>
                <div className="flex justify-between text-sm mt-1">
                  <span className="text-gray-500">Max Tab Switches:</span>
                  <span className="font-medium text-amber-600">{analytics.tab_switch_stats.max}</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Question Analysis */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* MCQ Analysis */}
          {analytics.mcq_analysis?.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="font-semibold text-gray-900 mb-4">MCQ Performance</h3>
              <div className="space-y-3">
                {analytics.mcq_analysis.map((mcq, idx) => (
                  <div key={mcq.mcq_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">Q{idx + 1}: {mcq.question}</p>
                      <p className="text-xs text-gray-500">{mcq.correct_count}/{mcq.total_attempts} correct</p>
                    </div>
                    <div className={`ml-4 px-3 py-1 rounded-full text-sm font-medium ${
                      mcq.accuracy >= 70 ? 'bg-green-100 text-green-700' :
                      mcq.accuracy >= 40 ? 'bg-yellow-100 text-yellow-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      {mcq.accuracy}%
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Coding Analysis */}
          {analytics.coding_analysis?.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Coding Problem Performance</h3>
              <div className="space-y-3">
                {analytics.coding_analysis.map((problem) => (
                  <div key={problem.problem_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">{problem.title}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`text-xs px-1.5 py-0.5 rounded ${
                          problem.difficulty === 'easy' ? 'bg-green-100 text-green-700' :
                          problem.difficulty === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {problem.difficulty}
                        </span>
                        <span className="text-xs text-gray-500">{problem.solved_count}/{problem.total_attempts} solved</span>
                      </div>
                    </div>
                    <div className={`ml-4 px-3 py-1 rounded-full text-sm font-medium ${
                      problem.success_rate >= 70 ? 'bg-green-100 text-green-700' :
                      problem.success_rate >= 40 ? 'bg-yellow-100 text-yellow-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      {problem.success_rate}%
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Candidates Table */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
            <h3 className="font-semibold text-gray-900">All Candidates ({candidates.length})</h3>
            <button
              onClick={handleAutoEvaluate}
              disabled={autoEvaluating}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
            >
              <Zap size={16} />
              {autoEvaluating ? 'Evaluating...' : 'Auto Evaluate'}
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Candidate</th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Score</th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase">MCQ</th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Coding</th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Result</th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Selection</th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {candidates.map(candidate => (
                  <tr key={candidate.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <p className="font-medium text-gray-900">{candidate.candidate_name}</p>
                      <p className="text-sm text-gray-500">{candidate.email}</p>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="text-lg font-bold text-gray-900">{candidate.percentage}%</span>
                      <p className="text-xs text-gray-500">{candidate.score}/{assessment.total_points}</p>
                    </td>
                    <td className="px-6 py-4 text-center text-sm text-gray-600">
                      {candidate.mcq_score || 0}
                    </td>
                    <td className="px-6 py-4 text-center text-sm text-gray-600">
                      {candidate.coding_score || 0}
                    </td>
                    <td className="px-6 py-4 text-center">
                      {candidate.passed ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                          <CheckCircle size={12} /> Passed
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
                          <XCircle size={12} /> Failed
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        candidate.selection_status === 'selected' ? 'bg-green-100 text-green-700' :
                        candidate.selection_status === 'next_round' ? 'bg-blue-100 text-blue-700' :
                        candidate.selection_status === 'rejected' ? 'bg-red-100 text-red-700' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {candidate.selection_status || 'Pending'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => updateCandidateSelection(candidate.id, 'selected')}
                          disabled={updatingSelection === candidate.id}
                          className={`p-1.5 rounded-lg transition-colors ${
                            candidate.selection_status === 'selected'
                              ? 'bg-green-500 text-white'
                              : 'bg-gray-100 text-gray-600 hover:bg-green-100 hover:text-green-600'
                          }`}
                          title="Select"
                        >
                          <UserCheck size={16} />
                        </button>
                        <button
                          onClick={() => updateCandidateSelection(candidate.id, 'next_round')}
                          disabled={updatingSelection === candidate.id}
                          className={`p-1.5 rounded-lg transition-colors ${
                            candidate.selection_status === 'next_round'
                              ? 'bg-blue-500 text-white'
                              : 'bg-gray-100 text-gray-600 hover:bg-blue-100 hover:text-blue-600'
                          }`}
                          title="Next Round"
                        >
                          <Zap size={16} />
                        </button>
                        <button
                          onClick={() => updateCandidateSelection(candidate.id, 'rejected')}
                          disabled={updatingSelection === candidate.id}
                          className={`p-1.5 rounded-lg transition-colors ${
                            candidate.selection_status === 'rejected'
                              ? 'bg-red-500 text-white'
                              : 'bg-gray-100 text-gray-600 hover:bg-red-100 hover:text-red-600'
                          }`}
                          title="Reject"
                        >
                          <UserX size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AssessmentAnalyticsPage;
